import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import { Vertical } from "@/components/Flex.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { WarmupSnapshotItem } from "@/components/WarmupSnapshots/WarmupSnapshotItem.tsx"
import { ellipsize } from "@/styles/textStyles.ts"

import type { WarmupSnapshotsViewModel } from "@/routes/projects/warmups/WarmupSnapshotsViewModel.ts"

interface WarmupSnapshotsListProps {
  model: WarmupSnapshotsViewModel
  canEditProject: boolean
}

export const WarmupSnapshotsList = observer(
  ({ model, canEditProject }: WarmupSnapshotsListProps) => {
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

    return (
      <Vertical gap="16px" width="100%">
        <ConfirmDialog
          open={confirmDialogOpen}
          onOpenChange={setConfirmDialogOpen}
          trigger={
            <CreateSnapshotButton
              kind="alternative"
              onClick={() => setConfirmDialogOpen(true)}
              disabled={!canEditProject}
            >
              Create snapshot
            </CreateSnapshotButton>
          }
          description="Are you sure you want to create a new snapshot in this project?"
          confirmKind="primary"
          confirmText="Create"
          onConfirm={() => {
            void model.createSnapshot()
            setConfirmDialogOpen(false)
          }}
        />

        {model.isLoading.get()
          ? <Spinner text={ellipsize("Loading")} />
          : (model.snapshots.get().length > 0 ? (
            <Vertical gap="12px">
              {model.snapshots.get().map((snapshot) => (
                <WarmupSnapshotItem
                  key={snapshot.id}
                  snapshot={snapshot}
                  deleteSnapshot={model.deleteSnapshot}
                  canEditProject={canEditProject}
                />
              ))}
            </Vertical>
          ) : (
            <EmptyState>
              No warmup snapshots exist. Create one to get started.
            </EmptyState>
          ))
        }
      </Vertical>
    )
  }
)

const CreateSnapshotButton = styled(Button)`
  align-self: flex-start;
`

const EmptyState = styled.div`
  color: var(--secondary-text-color);
  text-align: center;

  padding: 24px;

  border: 1px dashed var(--separator-color);
  border-radius: var(--border-radius-8);
`
